import { useContext, useState, useEffect } from 'react';
import AppContext from '../contexts/AppContext';
import ConfigContext from '../contexts/ConfigContext';
import DataContext from '../contexts/DataContext';
import { appendScript } from '../helpers';
import electricUtilities from './../data/providers';

import Button from './Button';
import CustomSelect from './CustomSelect';

export default function UtilityProvider() {
  const { features } = useContext(ConfigContext);
  const {
    slides,
    currentSlide,
    goToNextSlide,
    updateSelectedOption,
    changedState,
    setChangedState,
    enphaseAccTariffs,
  } = useContext(AppContext);
  const { data, updateData } = useContext(DataContext);

  const utilityBlacklist = [
    'Ameren',
    'Bandera Electric Coop, Inc',
    'CenterPoint Energy',
    'Magic Valley Electric Coop Inc',
  ];

  const [selectedOption, setSelectedOption]: [any, any] = useState(null);
  const [options, setOptions] = useState([]);

  const handleChange = (selectedOption: any) => {
    setSelectedOption(selectedOption);
    updateSelectedOption(slides[currentSlide].key, selectedOption.value);
    updateData({
      ...data,
      utilityProvider: selectedOption.value,
    });
    dataLayer.push({
      event: 'seform_electric_utility_provider',
      event_action: 'Who is your electric utility provider?',
      event_label: selectedOption!.value,
    });
    goToNextSlide();
  };

  const setEnphaseOptions = () => {
    if (Object.keys(enphaseAccTariffs).length === 0) return;
    const options: any = [];
    enphaseAccTariffs.plans.forEach((option: any) => {
      options.push({ value: option.lseName, label: option.lseName });
    });
    setOptions(options);
    if (!slides[currentSlide].selectedValue) {
      const selectedOption: any = {
        value: enphaseAccTariffs.prefLseName,
        label: enphaseAccTariffs.prefLseName,
      };
      setSelectedOption(selectedOption);
      updateSelectedOption(slides[currentSlide].key, selectedOption.value);
      updateData({
        ...data,
        utilityProvider: selectedOption.value,
      });
    }
  };

  const setEnphaseRateStructureOptions = () => {
    if (Object.keys(enphaseAccTariffs).length === 0 || !selectedOption) return;
    const options: any = [];
    const plan = enphaseAccTariffs.plans.find(
      (plan: any) => plan.lseName === (selectedOption as any).value
    );
    if (!plan) return;
    plan.tariffList.forEach((option: any) => {
      options.push({ value: option.tariffCode, label: option.tariffCode });
    });

    const selectedRateOption: any = {
      value: plan.tariffList[0].tariffCode,
      label: plan.tariffList[0].tariffCode,
    };
    updateData({
      ...data,
      enphaseRateSelectedOption: selectedRateOption.value,
    });
  };

  useEffect(() => {
    const options: any = [];
    if (features.showPanelPlacement) {
      setEnphaseOptions();
    } else {
      if (utilityBlacklist.indexOf(data.utility) !== -1) return;
      const state = data.selectedState?.toUpperCase();

      if (typeof (electricUtilities as any)[state] === 'undefined') return;

      let utilityArray = (electricUtilities as any)[state];
      const dashIndex = utilityArray.indexOf('----------');
      if (dashIndex > -1) {
        const staticArray = utilityArray.slice(0, dashIndex);
        const toSortArray = utilityArray
          .slice(dashIndex, utilityArray.length - 1)
          .sort();
        toSortArray.push('Not Listed');
        utilityArray = staticArray.concat(toSortArray);
      }

      utilityArray.forEach((utility: any) => {
        if (utility === '----------') {
          options.push({ value: utility, label: utility, isDisabled: true });
        } else {
          options.push({ value: utility, label: utility });
        }
      });
    }

    setOptions(options);

    if (!changedState && !features.useUtilityHeadline) {
      if (slides[currentSlide].selectedValue) {
        const selectedOption: any = {
          value: slides[currentSlide].selectedValue,
          label: slides[currentSlide].selectedValue,
        };
        setSelectedOption(selectedOption);
        updateSelectedOption(slides[currentSlide].key, selectedOption.value);

        const selectedRateOption: any = {
          value: slides[currentSlide].additionalFields.enphaseRateStructure,
          label: slides[currentSlide].additionalFields.enphaseRateStructure,
        };
        updateSelectedOption(
          slides[currentSlide].key,
          selectedRateOption.value,
          'enphaseRateStructure'
        );
        updateData({
          ...data,
          utilityProvider: selectedOption.value,
          enphaseRateSelectedOption: selectedRateOption.value,
        });
      }
    } else if (!features.useUtilityHeadline) {
      updateSelectedOption(slides[currentSlide].key, null);
      updateData({
        ...data,
        utilityProvider: null,
      });
      setChangedState(false);
    }

    if (features.useUtilityHeadline) {
      if (data.utilityProvider) {
        const selectedOption: any = {
          value: data.utilityProvider,
          label: data.utilityProvider,
        };
        setSelectedOption(selectedOption);
      }
    } else {
      if (data.selectedZipCode) {
        window.updateCity = function (response: any) {
          let selectedCity = response.data.placename || response.city;
          if (selectedCity) {
            updateData({ ...data, selectedCity });
          }
        };
        appendScript(
          `https://geoip.solarleadfactory.ai/zip2state/?callback=updateCity&zip=${data.selectedZipCode}`,
          true
        );
      }
    }
  }, []);

  useEffect(() => {
    setEnphaseOptions();
  }, [enphaseAccTariffs]);

  useEffect(() => {
    setEnphaseRateStructureOptions();
  }, [selectedOption]);

  return (
    <div>
      <p className="mt-[30px] md:mt-10 text-[16px] short:!text-[16px] md:text-[18px]">
        Electric Utility ssa
      </p>
      <div className="w-full short:!w-[350px] max-w-[279px] md:max-w-[400px] md:w-[400px] h-10 md:h-[57px] mt-3 md:mt-4 mb-6 md:mb-10 text-[14px]">
        {!features.showPanelPlacement ||
        (options.length > 0 && selectedOption) ? (
          <CustomSelect
            value={selectedOption}
            onChange={handleChange}
            options={options}
            placeholder="Select your utility provider"
            // zukoId="utility-provider-select"
          />
        ) : (
          <div className="w-full h-full flex items-center">
            <span className="loader-dots"></span>
          </div>
        )}
      </div>
      <div className="mt-[50px] md:mt-[60px]">
        <Button
          title="Continue"
          action={() => {
            updateData({
              ...data,
              utilityProvider: selectedOption!.value,
            });
            dataLayer.push({
              event: 'seform_electric_utility_provider',
              event_action: 'Who is your electric utility provider?',
              event_label: selectedOption!.value,
            });
            goToNextSlide();
          }}
          disabled={!slides[currentSlide].selectedValue}></Button>
      </div>
    </div>
  );
}
